import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, AlertTriangle, ArrowLeft, Lock } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import PadaidLogo from '../../components/ui/PadaidLogo';
import { consumePasswordResetLink } from '../../services/passwordReset';

const ResetPassword = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const email = params.get('email') || '';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const tokenMissing = useMemo(() => !token || !email, [token, email]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const newPassword = e.target.newPassword.value;
      if (!newPassword || newPassword.length < 8) {
        setError('Le mot de passe doit contenir au moins 8 caractères');
        setLoading(false);
        return;
      }
      await consumePasswordResetLink(token, email, newPassword);
      setSuccess(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Lien invalide ou expiré';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background/90 to-primary/5 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <Link to="/login" className="inline-flex items-center text-muted-foreground hover:text-primary mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour à la connexion
          </Link>
          <div className="flex justify-center mb-6">
            <PadaidLogo size="lg" showText={true} />
          </div>
          <h2 className="text-3xl font-bold text-foreground">Réinitialiser le mot de passe</h2>
          <p className="mt-2 text-muted-foreground">Choisissez un nouveau mot de passe sécurisé</p>
        </div>

        {tokenMissing ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center"><AlertTriangle className="mr-2 h-5 w-5 text-destructive" />Lien manquant</CardTitle>
              <CardDescription>Ce lien n'est pas valide. Veuillez demander un nouvel email de réinitialisation.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild className="w-full bg-primary hover:bg-primary/90">
                <Link to="/forgot-password">Demander un nouveau lien</Link>
              </Button>
            </CardContent>
          </Card>
        ) : success ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-green-600"><CheckCircle className="mr-2 h-5 w-5" />Mot de passe mis à jour</CardTitle>
              <CardDescription>Votre mot de passe a été changé avec succès.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full bg-primary hover:bg-primary/90" onClick={() => navigate('/login', { replace: true })}>
                Se connecter
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center"><Lock className="mr-2 h-5 w-5" />Nouveau mot de passe</CardTitle>
              <CardDescription>Pour: <span className="font-medium">{email}</span></CardDescription>
            </CardHeader>
            <CardContent>
              {error && (
                <Alert className="mb-4 border-destructive/20 bg-destructive/5">
                  <AlertTriangle className="h-4 w-4 text-destructive" />
                  <AlertDescription className="text-destructive">{error}</AlertDescription>
                </Alert>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="newPassword">Nouveau mot de passe</Label>
                  <Input id="newPassword" name="newPassword" type="password" minLength={8} required className="mt-1" placeholder="••••••••" />
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    • Au moins 8 caractères
                    <br />• Recommandé: lettres, chiffres et symboles
                  </p>
                </div>
                <Button type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={loading}>
                  {loading ? 'Réinitialisation...' : 'Mettre à jour le mot de passe'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;